import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { agentsApi, ComposeRegister } from '@/lib/agents';
import { AppShell } from '@/components/app/AppShell';
import { InlineAlert } from '@/components/app/Modal';
import { Dropdown } from '@/components/ui/Dropdown';
import { CitedMarkdown } from '@/components/ui/CitedMarkdown';
import { modelTierLabel } from '@/lib/model-label';
import { SaveResultButton } from '@/components/app/SaveResultButton';
import { ResultFooter } from '@/components/app/ResultFooter';
import { OutputLanguageSelect } from '@/components/app/OutputLanguageSelect';
import { HelpHint } from '@/components/app/HelpHint';
import { ExampleChips } from '@/components/app/ExampleChips';
import { AUDIENCE_PRESETS } from '@/lib/agentHelp';
import { LanguageCode, defaultOutputLanguage } from '@/i18n/languages';

const REGISTERS: ComposeRegister[] = ['first_contact_formal', 'follow_up', 'introduction', 'friendly'];

export function ComposePage() {
  const { t, i18n } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const [brief, setBrief] = useState('');
  const [register, setRegister] = useState<ComposeRegister>('follow_up');
  const [audience, setAudience] = useState('');
  const [language, setLanguage] = useState<LanguageCode>(defaultOutputLanguage(i18n.language));

  const compose = useMutation({
    mutationFn: () => agentsApi.compose(wsId!, { brief, register, audience, language }),
  });

  return (
    <AppShell
      title={t('compose.title', { defaultValue: 'Compose' })}
      description={t('compose.subtitle', { defaultValue: 'Draft outbound communication tuned to audience and keigo register.' }) as string}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-6">
        <HelpHint page="compose" />
        <div>
          <label className="block text-sm font-medium text-ink-900 mb-1">{t('compose.brief', { defaultValue: 'Brief' })}</label>
          <textarea
            value={brief}
            onChange={(e) => setBrief(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
                e.preventDefault();
                if (brief.trim() && !compose.isPending) compose.mutate();
              }
            }}
            placeholder={t('compose.brief_placeholder', { defaultValue: 'Describe what you want drafted, the audience, and any context. ↩ to draft, ⇧↩ for a new line.' }) as string}
            rows={4}
            className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
          />
          {!brief.trim() && (
            <div className="mt-3">
              <ExampleChips page="compose" onPick={setBrief} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('compose.register', { defaultValue: 'Keigo register' })}</label>
            <div className="w-full">
              <Dropdown<ComposeRegister>
                value={register}
                onChange={(next) => setRegister(next)}
                items={REGISTERS.map((r) => ({
                  value: r,
                  label: t(`compose.register.${r}`, { defaultValue: r }) as string,
                }))}
                ariaLabel={t('compose.register', { defaultValue: 'Keigo register' }) as string}
                size="md"
                className="w-full"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('compose.audience', { defaultValue: 'Audience' })}</label>
            <input
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (brief.trim() && !compose.isPending) compose.mutate();
                }
              }}
              placeholder={t('compose.audience_placeholder', { defaultValue: 'e.g. 部長 / steering committee' }) as string}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
            />
            <div className="mt-2">
              <ExampleChips
                items={AUDIENCE_PRESETS}
                label={t('help.common_audiences', { defaultValue: 'Common audiences' }) as string}
                onPick={setAudience}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-900 mb-1">{t('compose.language', { defaultValue: 'Output language' })}</label>
            <div className="w-full">
              <OutputLanguageSelect value={language} onChange={setLanguage} />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={() => compose.mutate()}
            disabled={!brief.trim() || compose.isPending}
            className="btn-press px-4 py-2 text-sm disabled:opacity-40"
          >
            {compose.isPending ? t('compose.drafting', { defaultValue: 'Drafting…' }) : t('compose.submit', { defaultValue: 'Draft' })}
          </button>
        </div>

        {compose.isError && (
          <InlineAlert tone="error">
            {String((compose.error as Error)?.message || 'Compose failed')}
          </InlineAlert>
        )}

        {compose.data && (
          <article className="card-paper p-5">
            <div className="text-xs text-ink-500 mb-2 flex items-center justify-between">
              <span>{compose.data.registerLabel}</span>
              <span>
                {modelTierLabel(compose.data.model)} · {compose.data.inputTokens + compose.data.outputTokens} tok · ¢{compose.data.costCents} · {compose.data.citedChunkIds.length} {t('ask.citations', { defaultValue: 'citations' })}
              </span>
            </div>
            <CitedMarkdown content={compose.data.draft} wsId={wsId} />
            <ResultFooter wsId={wsId!} citedChunkIds={compose.data.citedChunkIds} askSeed={brief}>
              <SaveResultButton
                workspaceId={wsId!}
                kind="compose"
                title={brief}
                output={compose.data.draft}
                input={{ brief, register, audience, language }}
                citedChunkIds={compose.data.citedChunkIds}
                model={compose.data.model}
                costCents={compose.data.costCents}
              />
            </ResultFooter>
          </article>
        )}
      </div>
    </AppShell>
  );
}
